import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { useBranch } from '../contexts/BranchContext';
import { House, Users, CalendarBlank, ClipboardText, ChartBar, SignOut, BellRinging, ListChecks, UserGear, Buildings, CaretDown, Package, ShoppingCart, GridFour, List, X, FileText, ClockClockwise, Star, MonitorPlay, ChartLineUp, Heart, Wallet, Receipt, CurrencyDollar, Robot, CreditCard, GraduationCap, Trash } from '@phosphor-icons/react';
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { playChime, notifyDesktop } from '../utils/alerts';
import BarcodeScanner from './BarcodeScanner';
import SubscriptionBanner from './SubscriptionBanner';

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { branches, activeBranch, switchBranch, loading } = useBranch();
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mainContentRef = useRef(null);

  // Prevent animated scroll on route change - scroll to top instantly without animation
  useEffect(() => {
    if (mainContentRef.current) {
      // Scroll to top instantly without animation
      mainContentRef.current.scrollTo({
        top: 0,
        behavior: 'instant' // No animation, instant jump
      });
    }
  }, [location.pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Activity heartbeat - silent monitoring (every 60 seconds)
  const sendActivityHeartbeat = useCallback(async (immediate = false) => {
    try {
      const pageTitle = document.title || 'Unknown Page';
      
      // Track tab visibility and window focus
      const isTabVisible = !document.hidden;
      const isWindowFocused = document.hasFocus();
      
      // Get last interaction time from state
      const idleTime = lastInteractionRef.current 
        ? Math.floor((Date.now() - lastInteractionRef.current) / 1000)
        : 0;
      
      await api.post('/activity/heartbeat', {
        current_page: location.pathname,
        page_title: pageTitle,
        timestamp: new Date().toISOString(),
        tab_visible: isTabVisible,
        window_focused: isWindowFocused,
        last_interaction_time: lastInteractionRef.current 
          ? new Date(lastInteractionRef.current).toISOString() 
          : new Date().toISOString(),
        idle_time_seconds: idleTime
      });
    } catch (error) {
      // Silent fail - don't interrupt user experience
      console.debug('Activity heartbeat failed:', error);
    }
  }, [location.pathname]);

  // Track user interactions (mouse, keyboard)
  const lastInteractionRef = useRef(Date.now());
  
  const updateLastInteraction = useCallback(() => {
    lastInteractionRef.current = Date.now();
  }, []);

  // INSTANT detection: Send heartbeat immediately when tab/window changes
  useEffect(() => {
    if (!user) return;

    // Tab visibility change - INSTANT detection
    const handleVisibilityChange = () => {
      console.log('Tab visibility changed:', document.hidden ? 'HIDDEN' : 'VISIBLE');
      sendActivityHeartbeat(true); // Send immediately
    };

    // Window focus/blur - INSTANT detection
    const handleWindowBlur = () => {
      console.log('Window LOST focus - User switched to another app');
      sendActivityHeartbeat(true); // Send immediately
    };

    const handleWindowFocus = () => {
      console.log('Window GAINED focus - User returned to app');
      sendActivityHeartbeat(true); // Send immediately
    };

    // Add event listeners for INSTANT detection
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [user, sendActivityHeartbeat]);

  useEffect(() => {
    if (user) {
      // Send initial heartbeat
      sendActivityHeartbeat();
      
      // Send heartbeat every 60 seconds (for regular updates)
      const interval = setInterval(() => sendActivityHeartbeat(false), 60000);
      
      // Track user interactions
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, updateLastInteraction);
      });
      
      return () => {
        clearInterval(interval);
        events.forEach(event => {
          document.removeEventListener(event, updateLastInteraction);
        });
      };
    }
  }, [user, sendActivityHeartbeat, updateLastInteraction]);

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: House, testId: 'nav-dashboard', menuId: 'dashboard', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'Customers', path: '/customers', icon: Users, testId: 'nav-customers', menuId: 'customers', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'Deleted', path: '/deleted', icon: Trash, testId: 'nav-deleted', menuId: 'deleted', roles: ['admin', 'owner', 'manager', 'receptionist', 'staff'] },
    { name: 'Batches', path: '/batches', icon: CalendarBlank, testId: 'nav-batches', menuId: 'batches', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'Batch Schedule', path: '/batch-bed-schedule', icon: GridFour, testId: 'nav-batch-schedule', menuId: 'batch-schedule', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'Premium Schedule', path: '/premium-schedule', icon: Star, testId: 'nav-premium-schedule', menuId: 'premium-schedule', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'Check-In', path: '/check-in', icon: ClipboardText, testId: 'nav-checkin', menuId: 'checkin', roles: ['admin', 'manager', 'receptionist'] },
    { name: 'Waiting List', path: '/waiting-list', icon: ListChecks, testId: 'nav-waiting-list', menuId: 'waiting-list', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'BMI', path: '/bmi', icon: Heart, testId: 'nav-bmi', menuId: 'bmi', roles: ['admin', 'manager', 'staff'] },
    { name: 'Tracking', path: '/tracking', icon: FileText, testId: 'nav-tracking', menuId: 'tracking', roles: ['admin', 'manager', 'staff'] },
    { name: 'Daily Check-In', path: '/daily-check-in', icon: CalendarBlank, testId: 'nav-daily-check-in', menuId: 'daily-check-in', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'Staff Login/Logout', path: '/staff-login-logout', icon: ClockClockwise, testId: 'nav-staff-login-logout', menuId: 'staff-login-logout', roles: ['admin', 'owner', 'manager', 'receptionist', 'staff'] },
    { name: 'Attendance', path: '/attendance-report', icon: ChartBar, testId: 'nav-attendance', menuId: 'attendance', roles: ['admin', 'manager', 'receptionist', 'staff'] },
    { name: 'Stock Items', path: '/stock-items', icon: Package, testId: 'nav-stock-items', menuId: 'stock-items', roles: ['admin', 'owner'] },
    { name: 'Stock Management', path: '/stock-management', icon: ShoppingCart, testId: 'nav-stock-management', menuId: 'stock', roles: ['admin', 'manager'] },
    { name: 'Company Ledger', path: '/company-ledger', icon: Wallet, testId: 'nav-company-ledger', menuId: 'company-ledger', roles: ['admin', 'owner', 'manager', 'receptionist', 'staff'] },
    { name: 'Sales Records', path: '/sales-records', icon: Receipt, testId: 'nav-sales-records', menuId: 'sales-records', roles: ['admin', 'owner', 'manager', 'receptionist', 'staff'] },
    { name: 'Petty Cash & Expenses', path: '/petty-cash', icon: CurrencyDollar, testId: 'nav-petty-cash', menuId: 'petty-cash', roles: ['admin', 'owner', 'manager', 'receptionist', 'staff'] },
    { name: 'Notifications', path: '/notifications', icon: BellRinging, testId: 'nav-notifications', menuId: 'notifications', roles: ['admin', 'manager'] },
    { name: 'Absent Calls', path: '/notifications-absent', icon: BellRinging, testId: 'nav-notifications-absent', menuId: 'notifications-absent', roles: ['admin', 'manager', 'owner', 'staff'] },
    { name: '🩺 Counselling', path: '/counselling', icon: Heart, testId: 'nav-counselling', menuId: 'counselling', roles: ['admin', 'manager', 'owner'] },
    { name: 'Analytics', path: '/analytics', icon: ChartBar, testId: 'nav-analytics', menuId: 'analytics', roles: ['admin', 'manager'] },
    { name: 'AI Assistant', path: '/ai-assistant', icon: Robot, testId: 'nav-ai-assistant', menuId: 'ai-assistant', roles: ['admin', 'owner', 'manager'] },
    { name: 'Branch Management', path: '/branch-management', icon: Buildings, testId: 'nav-branch-management', menuId: 'branches', roles: ['admin'] },
    { name: 'AI Settings', path: '/ai-settings', icon: GraduationCap, testId: 'nav-ai-settings', menuId: 'ai-settings', roles: ['admin'] },
    { name: '📅 Subscription', path: '/subscription', icon: CreditCard, testId: 'nav-subscription', menuId: 'subscription', roles: ['owner', 'admin'] },
    { name: '💳 AI Credits', path: '/branches/billing', icon: Wallet, testId: 'nav-ai-credits', menuId: 'ai-credits', roles: ['owner'] },
    { name: 'Users', path: '/users', icon: UserGear, testId: 'nav-users', menuId: 'users', roles: ['admin'] },
  ];

  const userRole = user?.role || 'staff';
  const userPermissions = user?.permissions || [];
  
  const visibleNavigation = navigation.filter(item => {
    // Admin sees every menu in every branch, regardless of stored permissions or branch subscription
    if (userRole === 'admin') {
      return true;
    }
    
    // CRITICAL: Always show owner-specific menu items to owners (bypass permissions)
    const ownerOnlyItems = ['ai-credits', 'subscription'];
    if (userRole === 'owner' && ownerOnlyItems.includes(item.menuId)) {
      return true;
    }
    
    // If user has custom permissions defined, use ONLY permissions (ignore role-based access)
    if (userPermissions.length > 0 && Array.isArray(userPermissions)) {
      return userPermissions.includes(item.menuId);
    }
    
    // Otherwise fall back to role-based filtering
    const hasRoleAccess = item.roles.includes(userRole);
    return hasRoleAccess;
  });

  // Pending edit-request badge for approvers (branch-scoped, polls + refreshes on window event)
  const [pendingEditRequests, setPendingEditRequests] = useState(0);
  const seenRequestIds = useRef(null);
  const canApprove = ['admin', 'owner', 'manager'].includes(user?.role);
  const fetchPendingCount = useCallback(async () => {
    if (!canApprove || !activeBranch?.branch_id) { setPendingEditRequests(0); return; }
    try {
      const res = await api.get('/edit-requests', { params: { branch_id: activeBranch.branch_id, status: 'pending' } });
      const reqs = res.data.requests || [];
      setPendingEditRequests(reqs.length);
      const ids = new Set(reqs.map(r => r.request_id));
      if (seenRequestIds.current) {
        const fresh = reqs.filter(r => !seenRequestIds.current.has(r.request_id));
        if (fresh.length > 0) {
          playChime();
          const r = fresh[0];
          notifyDesktop('New edit request', fresh.length === 1
            ? `${r.requested_by_name} requested edit access for ${r.customer_name}`
            : `${fresh.length} new edit requests awaiting approval`);
        }
      }
      seenRequestIds.current = ids;
    } catch (e) { /* ignore */ }
  }, [canApprove, activeBranch]);
  useEffect(() => { seenRequestIds.current = null; }, [activeBranch?.branch_id]);

  // Approved-edit badges for the requester (Batch Schedule / Premium Schedule)
  const [approvedEdits, setApprovedEdits] = useState({ regular: 0, premium: 0 });
  const seenApprovedIds = useRef(null);
  const fetchApprovedEdits = useCallback(async () => {
    if (!user || !activeBranch?.branch_id) return;
    try {
      const res = await api.get('/edit-requests/mine/approved', { params: { branch_id: activeBranch.branch_id } });
      setApprovedEdits({ regular: res.data.regular || 0, premium: res.data.premium || 0 });
      const reqs = res.data.requests || [];
      const ids = new Set(reqs.map(r => r.request_id));
      if (seenApprovedIds.current) {
        const fresh = reqs.filter(r => !seenApprovedIds.current.has(r.request_id));
        if (fresh.length > 0) {
          playChime();
          notifyDesktop('Edit access approved', `You can now edit notes for ${fresh[0].customer_name}${fresh.length > 1 ? ` and ${fresh.length - 1} more` : ''}`);
        }
      }
      seenApprovedIds.current = ids;
    } catch (e) { /* ignore */ }
  }, [user, activeBranch]);
  useEffect(() => { seenApprovedIds.current = null; }, [activeBranch?.branch_id]);
  useEffect(() => {
    fetchApprovedEdits();
    const t = setInterval(fetchApprovedEdits, 15000);
    window.addEventListener('edit-requests-changed', fetchApprovedEdits);
    return () => { clearInterval(t); window.removeEventListener('edit-requests-changed', fetchApprovedEdits); };
  }, [fetchApprovedEdits]);
  useEffect(() => {
    fetchPendingCount();
    const t = setInterval(fetchPendingCount, 15000);
    window.addEventListener('edit-requests-changed', fetchPendingCount);
    return () => { clearInterval(t); window.removeEventListener('edit-requests-changed', fetchPendingCount); };
  }, [fetchPendingCount]);
  const badges = { '/notifications': pendingEditRequests, '/batch-bed-schedule': approvedEdits.regular, '/premium-schedule': approvedEdits.premium };

  return (
    <LayoutContent 
      visibleNavigation={visibleNavigation}
      badges={badges}
      user={user}
      userRole={userRole}
      logout={logout}
      location={location}
      branches={branches}
      activeBranch={activeBranch}
      switchBranch={switchBranch}
      showBranchDropdown={showBranchDropdown}
      setShowBranchDropdown={setShowBranchDropdown}
      mobileMenuOpen={mobileMenuOpen}
      setMobileMenuOpen={setMobileMenuOpen}
      mainContentRef={mainContentRef}
    >
      {children}
    </LayoutContent>
  );
};

// SidebarContent component - moved outside to fix React lint warning
const SidebarContent = ({ visibleNavigation, badges = {}, user, userRole, logout, location }) => (
    <>
      <div className="p-4 lg:p-6 border-b border-[#E5E5DF]">
        <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_trial-batch-hub/artifacts/q9rnwiie_logo%20c.jpg" 
            alt="CERACARE Logo" 
            className="h-16 lg:h-12 w-auto object-contain"
          />
        </div>
        <h1 className="text-2xl lg:text-2xl font-semibold tracking-tight text-[#2C4C3B] text-center lg:text-left">CERACARE</h1>
        <p className="text-sm lg:text-sm text-[#1C1C1A]/60 mt-1 text-center lg:text-left">Batch Management System</p>
      </div>

      <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
        {visibleNavigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              className={`flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-md transition-colors text-sm lg:text-base ${
                isActive
                  ? 'bg-[#E2E8E4] text-[#2C4C3B] font-medium'
                  : 'text-[#1C1C1A]/70 hover:bg-[#E2E8E4]/50 hover:text-[#2C4C3B]'
              }`}
            >
              <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
              <span className="flex-1">{item.name}</span>
              {badges[item.path] > 0 && (
                <span
                  data-testid={`${item.testId}-badge`}
                  className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center animate-in fade-in"
                >
                  {badges[item.path] > 99 ? '99+' : badges[item.path]}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 lg:p-4 border-t border-[#E5E5DF]">
        <div className="px-3 lg:px-4 py-2 lg:py-3 mb-2">
          <p className="text-sm font-medium text-[#1C1C1A] truncate">{user?.name}</p>
          <p className="text-xs text-[#1C1C1A]/60 truncate">{user?.email}</p>
          <span className="inline-block mt-2 px-2 py-1 bg-[#2C4C3B] text-white text-xs font-medium rounded capitalize">
            {userRole}
          </span>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-md w-full text-[#C05C42] hover:bg-[#C05C42]/10 transition-colors text-sm lg:text-base"
        >
          <SignOut size={20} />
          Logout
        </button>
      </div>
    </>
);

// LayoutContent component - moved outside to fix React lint warning
const LayoutContent = ({ 
  visibleNavigation, 
  badges,
  user, 
  userRole, 
  logout, 
  location,
  branches,
  activeBranch,
  switchBranch,
  showBranchDropdown,
  setShowBranchDropdown,
  mobileMenuOpen,
  setMobileMenuOpen,
  mainContentRef,
  children
}) => {
  return (
    <div className="h-screen flex overflow-hidden bg-[#F9F9F8]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E5DF] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_trial-batch-hub/artifacts/q9rnwiie_logo%20c.jpg" 
            alt="CERACARE Logo" 
            className="h-10 w-auto object-contain"
          />
          <h1 className="text-xl font-semibold text-[#2C4C3B]">CERACARE</h1>
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-[#E2E8E4] rounded-md transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={28} weight="bold" className="text-[#2C4C3B]" /> : <List size={28} weight="bold" className="text-[#2C4C3B]" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Mobile (Drawer) */}
      <aside 
        className={`lg:hidden fixed top-[57px] bottom-0 left-0 w-72 bg-white border-r border-[#E5E5DF] z-40 flex flex-col transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
      >
        <SidebarContent visibleNavigation={visibleNavigation} badges={badges} user={user} userRole={userRole} logout={logout} location={location} />
      </aside>

      {/* Sidebar - Desktop (Fixed) */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-[#E5E5DF] flex-col fixed left-0 top-0 bottom-0 overflow-hidden" data-testid="sidebar">
        <SidebarContent visibleNavigation={visibleNavigation} badges={badges} user={user} userRole={userRole} logout={logout} location={location} />
      </aside>

      {/* Main Content Area (Scrollable) */}
      <main className="flex-1 lg:ml-64 overflow-y-auto h-screen pt-[57px] lg:pt-0">
        {/* Subscription Banner */}
        <SubscriptionBanner />
        
        {/* Branch Selector Header */}
        {branches.length > 0 && (
          <div className="sticky top-0 z-10 bg-white border-b border-[#E5E5DF] px-4 lg:px-6 py-3 lg:py-4">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-2 lg:gap-4">
                <Buildings size={20} className="text-[#2C4C3B] lg:w-6 lg:h-6" />
                <div className="relative">
                  <button
                    onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                    data-testid="branch-selector"
                    className="flex items-center gap-2 px-3 lg:px-4 py-1.5 lg:py-2 bg-[#E2E8E4] rounded-md hover:bg-[#D4DDD7] transition-colors text-sm lg:text-base"
                  >
                    <span className="font-medium text-[#2C4C3B] truncate max-w-[120px] lg:max-w-none">
                      {activeBranch?.name || 'Select Branch'}
                    </span>
                    <CaretDown size={16} className="text-[#2C4C3B]" />
                  </button>
                  
                  {showBranchDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-[#E5E5DF] py-2 z-20">
                      {branches.map((branch) => (
                        <button
                          key={branch.branch_id}
                          onClick={() => {
                            switchBranch(branch);
                            setShowBranchDropdown(false);
                          }}
                          data-testid={`branch-option-${branch.branch_id}`}
                          className={`w-full text-left px-4 py-2 hover:bg-[#E2E8E4] transition-colors ${
                            activeBranch?.branch_id === branch.branch_id ? 'bg-[#E2E8E4] text-[#2C4C3B] font-medium' : 'text-[#1C1C1A]'
                          }`}
                        >
                          <div className="font-medium">{branch.name}</div>
                          {branch.address && (
                            <div className="text-xs text-[#1C1C1A]/60">{branch.address}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="hidden lg:block text-sm text-[#1C1C1A]/60">
                {activeBranch?.address}
              </div>
            </div>
          </div>
        )}
        
        <div className="w-full max-w-7xl mx-auto px-4 lg:px-6 py-4 lg:py-8">
          {children}
        </div>
      </main>

      {/* Barcode Scanner Dialog */}
      <BarcodeScanner />
    </div>
  );
};

export default Layout;
